/**
 * Country flag and display-name helpers for proxy locations.
 *
 * The backend stores the proxy country as an ISO 3166-1 alpha-2 code whenever
 * one is provided (e.g. "DE"), and falls back to a plain country name when it is
 * not. Flags are rendered as emoji so no image assets or network requests are
 * needed.
 */

/** Offset from an ASCII letter to its REGIONAL INDICATOR SYMBOL code point. */
const REGIONAL_INDICATOR_OFFSET = 0x1f1e6 - 0x41

const nameCache = new Map<string, Intl.DisplayNames>()

function getDisplayNames(locale: string): Intl.DisplayNames | null {
  const cached = nameCache.get(locale)
  if (cached) return cached
  try {
    const names = new Intl.DisplayNames([locale], { type: "region" })
    nameCache.set(locale, names)
    return names
  } catch {
    // Unsupported locale or a runtime without Intl.DisplayNames.
    return null
  }
}

/** Normalizes an ISO 3166-1 alpha-2 code, or returns "" for anything else. */
export function toCountryCode(value?: string | null): string {
  const trimmed = (value ?? "").trim()
  return /^[A-Za-z]{2}$/.test(trimmed) ? trimmed.toUpperCase() : ""
}

/** Returns the flag emoji for a country code, or "" when it is not a code. */
export function countryFlag(value?: string | null): string {
  const code = toCountryCode(value)
  if (!code) return ""
  return Array.from(code)
    .map((letter) => String.fromCodePoint(letter.charCodeAt(0) + REGIONAL_INDICATOR_OFFSET))
    .join("")
}

/**
 * Returns the localized country name for a code. Plain country names (anything
 * that is not a two-letter code) are returned as they came in.
 */
export function countryName(value?: string | null, locale = "en", fallbackLocale = "en"): string {
  const raw = (value ?? "").trim()
  if (!raw) return ""
  const code = toCountryCode(raw)
  if (!code) return raw

  const names = getDisplayNames(locale) ?? getDisplayNames(fallbackLocale)
  try {
    return names?.of(code) ?? code
  } catch {
    return code
  }
}

/** Builds "Germany, Hamburg" (city omitted when empty). */
export function formatProxyLocation(
  proxy?: { country?: string | null; city?: string | null } | null,
  locale = "en",
  fallbackLocale = "en",
): string {
  if (!proxy) return ""
  const parts = [
    countryName(proxy.country, locale, fallbackLocale),
    (proxy.city ?? "").trim(),
  ].filter(Boolean)
  return parts.join(", ")
}
